import { Link } from "react-router-dom";
import { FormProvider } from "react-hook-form";
import { DefaultButton } from "../../../common/components/button/defaultButton/DefaultButton";
import { DefaultTextareaField } from "../../../common/components/forms/inputs/textareaField/DefaultTextareaField";
import { Icon, IconType } from "../../../common/icons/Icon";
import { useAiSupportDraftCard } from "../hooks/useAiSupportDraftCard";
import { IAiSupportDraft, aiAssistantTexts } from "../models/aiAssistantModels";
import styles from "./AiMessages.module.scss";

interface IAiSupportDraftCardProps {
  messageId: number | null;
  draft: IAiSupportDraft;
  supportChatId: string | null;
}

/** The ticket the assistant prepared for the Planny team; nothing is sent until the button. */
export const AiSupportDraftCard = ({ messageId, draft, supportChatId }: IAiSupportDraftCardProps) => {
  const { form, submit, isSending, canSend, sentChatHref, categoryLabel, priorityLabel } = useAiSupportDraftCard({
    messageId,
    draft,
    supportChatId,
  });

  return (
    <section className={styles.draft} aria-label={aiAssistantTexts.draftHeader}>
      <div className={styles.draft_header}>
        <Icon type={IconType.ChatBubbles} width="16px" height="16px" color="currentColor" />
        <span>{aiAssistantTexts.draftHeader}</span>
      </div>

      <div className={styles.draft_title}>{draft.title}</div>
      <div className={styles.draft_meta}>
        <span className={styles.draft_chip}>{categoryLabel}</span>
        <span className={styles.draft_chip}>{priorityLabel}</span>
      </div>

      {sentChatHref ? (
        <div className={styles.draft_sent}>
          <p className={styles.draft_sent_text}>{form.getValues("content") || draft.content}</p>
          <div className={styles.draft_sent_row}>
            <span className={styles.draft_sent_badge}>
              <Icon type={IconType.CheckmarkSentIcon} width="14px" height="14px" color="currentColor" />
              {aiAssistantTexts.draftSent}
            </span>
            <Link className={styles.draft_link} to={sentChatHref}>
              {aiAssistantTexts.draftOpen}
            </Link>
          </div>
        </div>
      ) : (
        <FormProvider {...form}>
          <form className={styles.draft_form} onSubmit={submit}>
            <p className={styles.draft_hint}>{aiAssistantTexts.draftHint}</p>
            <DefaultTextareaField name="content" rows={6} width="100%" disabled={isSending} />
            <div className={styles.draft_actions}>
              <DefaultButton
                type="submit"
                design="accent"
                width="auto"
                label={aiAssistantTexts.draftSend}
                loading={isSending}
                disabled={!canSend}
              />
            </div>
          </form>
        </FormProvider>
      )}
    </section>
  );
};
